import { motion, useReducedMotion } from "framer-motion";
import Screen from "../ui/Screen";
import { Check } from "../ui/icons";
import { shot } from "../data/shots";
import "./stack.css";

/**
 * The five things you set up once, as five boxes.
 *
 * Pricing is the big box because it is the first thing an owner checks the
 * product can express. Each box shows the real screen rising out of its
 * bottom edge rather than a description of it.
 */
const TILES = [
  {
    slug: "plans",
    area: "plans",
    title: "Your pricing, exactly as you sell it",
    sub: "Monthly through annual, personal training on top, and the discounts you actually give.",
    points: [
      "Any number of plans, any duration",
      "Change a price without touching a running membership",
      "Personal training priced separately",
    ],
  },
  {
    slug: "batches",
    area: "batches",
    title: "Morning, evening, and who runs them",
    sub: "Days, times, capacity and a trainer on each slot, with a check-in filter.",
  },
  {
    slug: "exercises",
    area: "exercises",
    title: "Exercises and workout plans",
    sub: "Add your own movements, build day-by-day templates and assign them to members. Share the routine as a PDF.",
  },
  {
    slug: "members-add",
    area: "add",
    title: "A new member in three steps",
    sub: "Name, dates and fee, first payment. A plan is optional. Nothing to finish later.",
  },
  {
    slug: "profile",
    area: "profile",
    title: "Your club's details, on every receipt",
    sub: "Address, hours and photos of the floor. Fill it in once. Receipts and statements are PDFs you share.",
  },
];

export default function Stack() {
  const reduce = useReducedMotion();

  return (
    <section className="section stack" id="setup">
      <div className="container container--wide stack__head">
        <h2 className="t-display-2">
          Set it up once,
          <br />
          then stop thinking about it.
        </h2>
        <p className="t-lead stack__lead">
          What you set in your first week and rarely touch again.
        </p>
      </div>

      <div className="container container--wide">
        <ul className="stack__grid">
          {TILES.map((tile, i) => {
            const meta = shot(tile.slug);
            return (
              <motion.li
                key={tile.slug}
                className={`stack__tile stack__tile--${tile.area}`}
                initial={reduce ? false : { opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.06 }}
              >
                <div className="stack__body">
                  <h3 className="stack__title">{tile.title}</h3>
                  <p className="stack__sub">{tile.sub}</p>
                  {tile.points && (
                    <ul className="stack__points">
                      {tile.points.map((point) => (
                        <li className="stack__point" key={point}>
                          <Check />
                          {point}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="stack__art">
                  <Screen
                    slug={tile.slug}
                    className="stack__phone"
                    alt={`The ${meta.screen} screen. ${meta.inFrame}`}
                    buttons={false}
                  />
                </div>
              </motion.li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
